import { motion } from "framer-motion";
import { SiGmail, SiGooglecalendar, SiNotion, SiSlack, SiLinear, SiJira, SiSalesforce, SiHubspot } from "react-icons/si";
import { Mail, Calendar, TrendingUp, Zap, Send } from "lucide-react";

const INTEGRATIONS = [
  { name: "Gmail",            Icon: SiGmail,          color: "#EA4335", category: "Email"        },
  { name: "Google Calendar",  Icon: SiGooglecalendar, color: "#4285F4", category: "Calendar"     },
  { name: "Salesforce",       Icon: SiSalesforce,     color: "#00A1E0", category: "CRM"          },
  { name: "Slack",            Icon: SiSlack,          color: "#E01E5A", category: "Messaging"    },
  { name: "Notion",           Icon: SiNotion,         color: "#FFFFFF", category: "Docs"         },
  { name: "Jira",             Icon: SiJira,           color: "#2684FF", category: "Project Mgmt" },
  { name: "Linear",           Icon: SiLinear,         color: "#5E6AD2", category: "Project Mgmt" },
  { name: "Outlook Mail",     Icon: Mail,             color: "#0078D4", category: "Email"        },
  { name: "Outlook Calendar", Icon: Calendar,         color: "#0078D4", category: "Calendar"     },
  { name: "HubSpot",          Icon: SiHubspot,        color: "#FF7A59", category: "CRM", soon: true },
];

function IntegrationTile({ integration, index }) {
  const { Icon, name, color, category, soon } = integration;
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.4, delay: index * 0.045, ease: [0.22, 1, 0.36, 1] }}
      whileHover={!soon ? { y: -5, transition: { duration: 0.2 } } : {}}
      className="group relative flex flex-col items-center gap-4 rounded-2xl p-5 sm:p-6 overflow-hidden cursor-default"
      style={{
        background: `linear-gradient(145deg, ${color}0E 0%, rgba(255,255,255,0.02) 100%)`,
        border: "1px solid rgba(255,255,255,0.07)",
      }}
    >
      {/* Hover glow overlay */}
      {!soon && (
        <div
          className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
          style={{
            background: `radial-gradient(circle at 50% 30%, ${color}14 0%, transparent 65%)`,
            border: `1px solid ${color}30`,
          }}
        />
      )}

      {soon && (
        <span className="absolute top-2.5 right-2.5 z-10 text-[9px] font-semibold uppercase tracking-widest bg-white/[0.12] text-white/60 px-1.5 py-0.5 rounded-full">
          Soon
        </span>
      )}

      {/* Icon */}
      <div
        className="relative z-10 flex h-12 w-12 items-center justify-center rounded-xl transition-transform duration-300 group-hover:scale-110"
        style={{
          backgroundColor: `${color}1C`,
          boxShadow: `0 0 0 1px ${color}28`,
        }}
      >
        <Icon
          className="h-6 w-6"
          style={{ color: soon ? `${color}50` : color }}
        />
      </div>

      {/* Label */}
      <div className="relative z-10 text-center">
        <p className={`text-[12.5px] font-medium leading-tight transition-colors duration-300 ${
          soon ? "text-white/25" : "text-white/70 group-hover:text-white/95"
        }`}>
          {name}
        </p>
        <p className="mt-0.5 text-[9.5px] uppercase tracking-widest text-white/25">{category}</p>
      </div>
    </motion.div>
  );
}


export default function IntegrationsSection() {
  return (
    <section id="integrations" className="relative scroll-mt-24 bg-[#070D1A] py-20 sm:py-28 overflow-hidden">
      {/* Dot grid */}
      <div
        className="pointer-events-none absolute inset-0 opacity-50"
        style={{
          backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.04) 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }}
      />
      {/* Ambient glow */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute left-1/2 top-1/3 h-[500px] w-[700px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#0092B4]/[0.08] blur-[140px]" />
      </div>

      <div className="relative mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">

        {/* ── Header ── */}
        <div className="mx-auto max-w-3xl text-center mb-12">
          <p className="text-sm font-semibold uppercase tracking-widest text-[#0092B4] mb-3">Integrations</p>
          <h2 className="text-[clamp(1.7rem,3.8vw,2.75rem)] font-bold tracking-tight text-white">
            Shadow plugs into the tools your team{" "}
            <span className="text-[#0092B4]">already uses.</span>
          </h2>
          <p className="mt-4 text-base text-slate-400 max-w-xl mx-auto">
            Your reps never leave their existing tools. Shadow reads context in and pushes completed work out — automatically.
          </p>
        </div>

        {/* ── Integration grid ── */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-5">
          {INTEGRATIONS.map((integration, i) => (
            <IntegrationTile
              key={integration.name}
              integration={integration}
              index={i}
            />
          ))}
        </div>

        {/* ── Category summary ── */}
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
          {[
            {
              Icon: TrendingUp,
              color: "#00A1E0",
              title: "Pipeline always in sync",
              body: "Stage updates, activity logs, and close dates written to your CRM after every call — zero rep input required.",
              tools: [
                { Icon: SiSalesforce, name: "Salesforce", color: "#00A1E0" },
                { Icon: SiHubspot,    name: "HubSpot",    color: "#FF7A59" },
              ],
            },
            {
              Icon: Zap,
              color: "#5E6AD2",
              title: "Tasks auto-created with full context",
              body: "Action items land in your project tools — assigned, contextualized, and ready to execute without a debrief.",
              tools: [
                { Icon: SiJira,   name: "Jira",   color: "#2684FF" },
                { Icon: SiLinear, name: "Linear", color: "#5E6AD2" },
                { Icon: SiNotion, name: "Notion", color: "#e5e5e5" },
              ],
            },
            {
              Icon: Send,
              color: "#EA4335",
              title: "Emails drafted and ready to send",
              body: "Personalized follow-ups, summaries, and channel updates written in your voice — sent before the day ends.",
              tools: [
                { Icon: SiGmail, name: "Gmail",   color: "#EA4335" },
                { Icon: SiSlack, name: "Slack",   color: "#E01E5A" },
                { Icon: Mail,    name: "Outlook", color: "#0078D4" },
              ],
            },
          ].map(col => (
            <div
              key={col.title}
              className="relative rounded-2xl border border-white/[0.08] p-6 overflow-hidden"
              style={{ background: `linear-gradient(140deg, ${col.color}0C 0%, rgba(255,255,255,0.02) 100%)` }}
            >
              {/* Category icon */}
              <div
                className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl"
                style={{ backgroundColor: `${col.color}1F`, boxShadow: `0 0 0 1px ${col.color}30` }}
              >
                <col.Icon className="h-5 w-5" style={{ color: col.color }} strokeWidth={2} />
              </div>

              {/* Title */}
              <h3 className="text-[14.5px] font-semibold text-white mb-2">{col.title}</h3>

              {/* Body */}
              <p className="text-[12.5px] text-white/50 leading-relaxed mb-5">{col.body}</p>

              {/* Tool chips with brand icons */}
              <div className="flex flex-wrap gap-2">
                {col.tools.map(({ Icon: ToolIcon, name, color: tc }) => (
                  <div
                    key={name}
                    className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5"
                    style={{ backgroundColor: `${tc}18` }}
                  >
                    <ToolIcon className="h-3.5 w-3.5 shrink-0" style={{ color: tc }} />
                    <span className="text-[11px] font-semibold" style={{ color: tc }}>{name}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
